import { useState, useEffect, useCallback } from "react";
import {
  fetchDdtQueueMessages,
  deleteDdtQueueMessage,
  clearDdtQueue,
} from "../../api/client";
import { useAuth } from "../../auth/useAuth";
import { canManageQueue } from "../../auth/permissions";
import type { DdtQueueMessage, DdtQueueMessagesResponse, QueueStatus } from "../../api/types";
import { useToast } from "../../context/ToastContext";
import { ConfirmModal } from "../../components/ConfirmModal";
import { QueueStatusFilter, QUEUE_STATUS_LABEL } from "../../lib/QueueStatusFilter";

/**
 * Coda output DDT (Item 1, 2026-07-14) — peek della coda Azure 'upsert-ddt',
 * stesso pattern di QueuePage.tsx (coda RDI) ma senza la sezione allegati blob
 * (il modulo DDT non usa azure_blob) e con i campi summary del contratto DDT
 * (_extract_ddt_summary in src/api/routes/queue.py) invece di quelli RDI.
 * A differenza della coda RDI, eliminare un messaggio qui NON riapre nessuna
 * pratica (il payload upsert-ddt non porta l'id della pratica — vedi nota nel
 * backend) — il ConfirmModal lo segnala esplicitamente.
 */
export function DdtQueue() {
  return (
    <div>
      <h1 className="page-title">
        <span className="material-symbols-outlined icon-lg" style={{ marginRight: "var(--space-2)" }}>outbox</span>
        Coda Output DDT
      </h1>
      <DdtQueueTab />
    </div>
  );
}

function DdtQueueTab() {
  const { addToast } = useToast();
  const { user } = useAuth();
  const canWrite = canManageQueue(user?.roles ?? []);
  const [data, setData] = useState<DdtQueueMessagesResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  // Paginazione (punto 4, 2026-07-14): la lista viene ora da DB, non piu' da
  // un peek limitato della coda Azure — resta per sempre (anche gli elementi
  // gia' prelevati), va paginata.
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;
  // Filtro a 3 stati (2026-07-28): Da prelevare (default) / Prelevati / Tutti.
  const [status, setStatus] = useState<QueueStatus>("pending");

  const load = useCallback(() => {
    setIsLoading(true);
    fetchDdtQueueMessages(page, PAGE_SIZE, status)
      .then((d) => { setData(d); setError(null); })
      .catch((e) => setError(e.message))
      .finally(() => setIsLoading(false));
  }, [page, status]);

  useEffect(() => { load(); }, [load]);

  const handleClearQueue = async () => {
    try {
      await clearDdtQueue();
      addToast("success", "Coda svuotata");
      setConfirmClear(false);
      load();
    } catch (e) {
      addToast("error", `Errore: ${(e as Error).message}`);
    }
  };

  const handleDeleteMessage = async (messageId: string) => {
    try {
      const res = await deleteDdtQueueMessage(messageId);
      addToast("success", res.already_picked_up
        ? "Il messaggio era già stato prelevato nel frattempo"
        : "Messaggio eliminato dalla coda");
      setConfirmDeleteId(null);
      load();
    } catch (e) {
      const msg = (e as Error).message;
      addToast(msg.includes("404") ? "info" : "error", msg.includes("404") ? "Pratica non trovata" : `Errore: ${msg}`);
      setConfirmDeleteId(null);
      load();
    }
  };

  const handleDownloadJson = (msg: DdtQueueMessage) => {
    const json = JSON.stringify(msg.content, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${msg.summary.numero_ddt || msg.message_id}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const messages = data?.messages ?? [];

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "var(--space-3)", flexWrap: "wrap", rowGap: "var(--space-2)" }}>
        <span className="text-muted" style={{ fontSize: "var(--font-size-sm)", minWidth: 0 }}>
          {data ? `${data.total} ${QUEUE_STATUS_LABEL[status]}` : "Caricamento..."}
          {data?.queue_name ? ` (${data.queue_name})` : ""}
        </span>
        <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap", justifyContent: "flex-end", alignItems: "center" }}>
          <QueueStatusFilter value={status} counts={data?.counts} onChange={(s) => { setPage(1); setStatus(s); }} />
          {messages.length > 0 && canWrite && (
            <button className="btn btn-danger btn-sm" onClick={() => setConfirmClear(true)}>
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>delete_sweep</span>
              Svuota coda
            </button>
          )}
          <button className="btn btn-ghost btn-sm" onClick={load} disabled={isLoading}>
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>refresh</span>
            Aggiorna
          </button>
        </div>
      </div>

      {(status === "in_lease" || messages.some((m) => m.state === "in_lease")) && (
        <div className="text-muted text-xs" style={{ marginBottom: "var(--space-3)" }}>
          In lavorazione: il gestionale del cliente ha preso in carico la pratica. Se non la conferma
          entro il tempo del lease, torna in coda da sola.
        </div>
      )}

      {isLoading && !data && <div className="loading-center"><div className="spinner spinner-lg" /></div>}

      {error && (
        <div className="card" style={{ background: "var(--indaco-danger-light)", color: "var(--indaco-danger)", padding: "var(--space-3)" }}>
          <span className="material-symbols-outlined" style={{ fontSize: 16, marginRight: 4 }}>error</span>
          Errore: {error}
        </div>
      )}

      {data?.peek_incomplete && (
        <div className="card" style={{ background: "var(--indaco-warning-light)", color: "var(--indaco-warning)", padding: "var(--space-3)", marginBottom: "var(--space-3)" }}>
          <span className="material-symbols-outlined" style={{ fontSize: 16, marginRight: 4, verticalAlign: "middle" }}>warning</span>
          Ci sono più di 32 messaggi in coda: sopra questa soglia lo stato "in lavorazione" non è determinabile per singolo messaggio e tutto è mostrato come "in coda".
        </div>
      )}

      {data && messages.length === 0 && !isLoading && (
        <div className="card text-center text-muted" style={{ padding: "var(--space-10)" }}>
          <span className="material-symbols-outlined" style={{ fontSize: 48, display: "block", marginBottom: "var(--space-2)", opacity: 0.4 }}>inbox</span>
          Coda vuota &mdash; tutti i DDT approvati sono stati consumati
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)", opacity: isLoading && data ? 0.6 : 1 }}>
        {messages.map((msg) => {
          const s = msg.summary;
          const isExpanded = expandedId === msg.message_id;

          return (
            <div key={msg.message_id} className="card" style={{ padding: 0, overflow: "hidden" }}>
              <div style={{ padding: "var(--space-3)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", marginBottom: "var(--space-1)" }}>
                  <span style={{ fontWeight: "var(--font-weight-semibold)", fontSize: "var(--font-size-base)", flex: 1 }}>
                    DDT {s.numero_ddt || "sconosciuto"}
                  </span>
                  {s.n_righe > 0 && <span className="badge badge-info">{s.n_righe} righe</span>}
                  {msg.state === "in_lease" && (
                    <span className="badge badge-warning"
                      title={`In lavorazione dal ${msg.in_lease_since ? formatDate(msg.in_lease_since) : "?"} — il gestionale del cliente l'ha presa in carico: se non la conferma entro il lease, la pratica torna in coda.`}>
                      In lavorazione
                    </span>
                  )}
                  {msg.picked_up_at && (
                    <span className="badge badge-neutral" title={`Non più in coda dal ${formatDate(msg.picked_up_at)}`}>
                      Prelevato
                    </span>
                  )}
                </div>

                {s.fornitore_testo && (
                  <div className="text-sm text-muted" style={{ marginBottom: "var(--space-1)" }}>
                    {s.fornitore_testo}{s.fornitore_codice ? ` (${s.fornitore_codice})` : ""}
                  </div>
                )}

                <div style={{
                  display: "grid", gridTemplateColumns: "auto 1fr", gap: "2px var(--space-3)",
                  fontSize: "var(--font-size-sm)", marginBottom: "var(--space-2)", marginTop: "var(--space-2)",
                }}>
                  {s.ordine_acquisto && (
                    <>
                      <span className="text-muted">Ordine acquisto</span>
                      <span>{s.ordine_acquisto}</span>
                    </>
                  )}
                  {s.data_ora && (
                    <>
                      <span className="text-muted">Data documento</span>
                      <span>{s.data_ora}</span>
                    </>
                  )}
                  {s.commesse_riferite.length > 0 && (
                    <>
                      <span className="text-muted">Commesse</span>
                      <span>{s.commesse_riferite.join(", ")}</span>
                    </>
                  )}
                  {s.nota_operatore && (
                    <>
                      <span className="text-muted">Nota operatore</span>
                      <span>{s.nota_operatore}</span>
                    </>
                  )}
                </div>

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "var(--space-2)" }}>
                  <span />
                  {msg.inserted_on && (
                    <span className="text-muted" style={{ fontSize: "var(--font-size-xs)", whiteSpace: "nowrap" }}>
                      In coda dal {formatDate(msg.inserted_on)}
                    </span>
                  )}
                </div>

                <div style={{ display: "flex", gap: "var(--space-2)", borderTop: "1px solid var(--indaco-gray-100)", paddingTop: "var(--space-2)" }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => setExpandedId(isExpanded ? null : msg.message_id)}>
                    <span className="material-symbols-outlined" style={{ fontSize: 16 }}>{isExpanded ? "visibility_off" : "data_object"}</span>
                    {isExpanded ? "Nascondi JSON" : "Vedi JSON"}
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={() => handleDownloadJson(msg)}>
                    <span className="material-symbols-outlined" style={{ fontSize: 16 }}>download</span>
                    Scarica JSON
                  </button>
                  {canWrite && !msg.picked_up_at && (
                    <button className="btn btn-danger btn-sm" style={{ marginLeft: "auto" }} onClick={() => setConfirmDeleteId(msg.message_id)}>
                      <span className="material-symbols-outlined" style={{ fontSize: 16 }}>delete</span>
                      Elimina
                    </button>
                  )}
                </div>
              </div>

              {isExpanded && (
                <pre style={{
                  margin: 0, padding: "var(--space-3)",
                  background: "var(--indaco-gray-50)", borderTop: "1px solid var(--indaco-gray-200)",
                  fontSize: "var(--font-size-xs)", overflow: "auto", maxHeight: 400,
                  whiteSpace: "pre-wrap", wordBreak: "break-word",
                }}>
                  {JSON.stringify(msg.content, null, 2)}
                </pre>
              )}
            </div>
          );
        })}
      </div>

      {data && data.total > PAGE_SIZE && (
        <div className="pagination">
          <button className="btn btn-ghost btn-sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>chevron_left</span>
          </button>
          <span className="text-muted text-sm">
            Pagina {page} di {Math.max(1, Math.ceil(data.total / PAGE_SIZE))} &mdash; {data.total} totali
          </span>
          <button className="btn btn-ghost btn-sm" disabled={page * PAGE_SIZE >= data.total} onClick={() => setPage((p) => p + 1)}>
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>chevron_right</span>
          </button>
        </div>
      )}

      {confirmClear && (
        <ConfirmModal
          title="Svuota coda output DDT"
          message={`Vuoi davvero svuotare la coda? Tutti i ${messages.length} messaggi verranno eliminati. A differenza della coda RDI, nessuna pratica verrà riaperta automaticamente.`}
          confirmLabel="Svuota"
          danger
          onConfirm={handleClearQueue}
          onCancel={() => setConfirmClear(false)}
        />
      )}
      {confirmDeleteId && (
        <ConfirmModal
          title="Elimina messaggio"
          message="Vuoi davvero eliminare questo messaggio dalla coda? A differenza della coda RDI, la pratica DDT corrispondente non verrà riaperta automaticamente."
          confirmLabel="Elimina"
          danger
          onConfirm={() => handleDeleteMessage(confirmDeleteId)}
          onCancel={() => setConfirmDeleteId(null)}
        />
      )}
    </>
  );
}

function formatDate(iso: string) {
  try {
    return new Date(iso).toLocaleDateString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}
